// Authentication service for handling login and registration

import { API_BASE_URL } from '../../config/api';

const AUTH_BASE_URL = `${API_BASE_URL}/shared`;

export const authService = {
  // Login function
  async login(email, password) {
    try {
      console.log('Attempting login with:', { email });
      
      const response = await fetch(`${AUTH_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();
      console.log('Login response data:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Login failed');
      }

      if (data.token) {
        // Store authentication data
        this.storeAuthData(data);
        return { success: true, data };
      }

      throw new Error('No token received');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Store authentication data in localStorage
  storeAuthData(data) {
    const userRole = data.role || (data.user && data.user.role);
    console.log('User role from response:', userRole);
    
    // Normalize role to lowercase for comparison
    const normalizedRole = userRole ? userRole.toLowerCase().trim() : '';
    console.log('Normalized user role:', normalizedRole);
    
    // Store JWT token
    localStorage.setItem('token', data.token);
    
    // Extract and store user ID from multiple sources for reliability
    let userId = data.user?.user_id || data.user?.id || data.user_id || data.userId || data.id;
    if (userId) {
      localStorage.setItem('userId', userId.toString());
      console.log('User ID stored:', userId);
    } else {
      console.warn('No user ID found in response');
    }
    
    // Store complete user info object with enhanced data
    const userInfo = {
      user_id: data.user?.user_id || data.user_id || data.user?.id || data.userId || data.id,
      id: data.user?.user_id || data.user?.id || data.userId || data.id,
      email: data.user?.email || data.email,
      name: data.user?.name || data.name || data.user?.firstname,
      surname: data.user?.surname || data.user?.lastname,
      role: data.user?.role || data.role,
      firstname: data.user?.firstname,
      lastname: data.user?.lastname || data.user?.surname,
      cellnumber: data.user?.cellnumber || data.user?.phone,
      institution: data.user?.institution,
      ieee_no: data.user?.ieee_no,
      vat_no: data.user?.vat_no,
      faculty_id: data.user?.faculty_id,
      department_id: data.user?.department_id
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    console.log('User info stored:', userInfo);
    
    // Store user role for future redirection checks
    const finalUserRole = data.user?.role || data.role || userRole;
    if (finalUserRole) {
      localStorage.setItem('userRole', finalUserRole);
      console.log('User role stored:', finalUserRole);
    } else {
      console.warn('No user role found in response');
    }
    
    // Store user type if available
    if (data.userType || data.user?.userType) {
      localStorage.setItem('userType', data.userType || data.user?.userType);
    }
    
    // Store any additional authentication data
    if (data.user) {
      Object.keys(data.user).forEach(key => {
        if (!['password', 'password_hash'].includes(key)) {
          const value = data.user[key];
          if (value !== undefined && value !== null) {
            localStorage.setItem(`user_${key}`, typeof value === 'object' ? JSON.stringify(value) : value.toString());
          }
        }
      });
    }
    
    // Validate authentication state after storage
    const authValidation = {
      hasToken: !!localStorage.getItem('token'),
      hasUserId: !!localStorage.getItem('userId'),
      hasUserRole: !!localStorage.getItem('userRole'),
      hasUserInfo: !!localStorage.getItem('userInfo'),
      token: localStorage.getItem('token') ? 'exists' : 'missing',
      userId: localStorage.getItem('userId'),
      userRole: localStorage.getItem('userRole')
    };
    console.log('Authentication validation after login:', authValidation);
    
    return normalizedRole;
  },

  // Get redirection path based on user role
  getRedirectPath(userRole) {
    const normalizedRole = userRole ? userRole.toLowerCase().trim() : '';
    
    if (normalizedRole === 'admin') {
      console.log('Redirecting to admin dashboard');
      return '/admin';
    } else if (normalizedRole === 'organizer' || normalizedRole === 'organiser') {
      console.log('Redirecting to organizer dashboard');
      return '/organiser-dash';
    } else {
      console.log(`Redirecting to main menu for role: ${userRole}`);
      return '/mainmenu';
    }
  },

  // Registration preparation (stores data for next step)
  prepareRegistration(email, password) {
    localStorage.setItem("reg_email", email);
    localStorage.setItem("reg_password", password);
  },

  // Clear all authentication data
  clearAuthData() {
    console.log("🏠 Landing page loaded - clearing authentication data");
    
    // Clear all authentication-related data from localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    localStorage.removeItem("userId");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    
    // Clear any session storage data
    sessionStorage.clear();
    
    console.log("✅ Authentication data cleared from landing page");
  }
};
